/**
 * App-wide PrimeVue pass-through: accessibility fixes applied once, at the
 * `pt` level, instead of at every component site. Registered in `main.ts`; a
 * site's own `:pt` still wins key by key (PrimeVue merges global, then self).
 * Never patch `node_modules` for these.
 */
import type { PrimeVuePTOptions } from 'primevue/config';

type Attrs = Record<string, unknown>;

/** The name a site gave `<DataTable>`: `aria-label` or `aria-labelledby`. */
function dataTableName(attrs: Attrs | undefined): Attrs {
  const name: Attrs = {};
  if (attrs?.['aria-label']) name['aria-label'] = attrs['aria-label'];
  if (attrs?.['aria-labelledby']) name['aria-labelledby'] = attrs['aria-labelledby'];
  return name;
}

export const adminPassThrough: PrimeVuePTOptions = {
  datatable: {
    // WCAG 4.1.2 (audit A48). DataTable falls attributes through to its root
    // <div>, which has no role, so `aria-label` there is prohibited and the
    // <table> stays unnamed. Take the name off the root...
    root: ({ attrs }: { attrs: Attrs }) => {
      const name = dataTableName(attrs);
      return Object.fromEntries(Object.keys(name).map((key) => [key, undefined]));
    },
    // ...and put it on the table itself.
    table: ({ attrs }: { attrs: Attrs }) => dataTableName(attrs),
    // WCAG 2.1.1 (audit A25). The table container is `overflow: auto`. When it
    // overflows with nothing focusable inside (an empty state, or any table at
    // phone widths) the keyboard cannot scroll it. Make it a focusable region,
    // named after the table when the site named it. Recent Chrome makes such
    // scrollers focusable on its own; Safari does not. No upstream issue.
    tableContainer: ({ attrs }: { attrs: Attrs }) => {
      const name = dataTableName(attrs);
      return Object.keys(name).length ? { tabindex: 0, role: 'region', ...name } : { tabindex: 0 };
    },
  },
  // WCAG 1.3.1, 4.1.2 (audit A24). PrimeVue 5.0.2 sets role="tablist" on the
  // Stepper ROOT, so the step panels (role="tabpanel") sit inside the tablist
  // beside the tabs (axe aria-required-children, critical). Move the role to
  // StepList, which holds only the steps (role="presentation" around each
  // role="tab"). Each step's wrapper also carries `aria-current`, a global
  // attribute that cancels role="presentation", so that moves to the tab, with
  // the `aria-selected` the tab never had. Upstream: primefaces/primevue#7939,
  // fix in PR #8602.
  stepper: {
    root: { role: undefined },
  },
  steplist: {
    root: { role: 'tablist' },
  },
  // APG dialog pattern (audit A29). Select's Escape handler stops propagation
  // even when its list is closed (`//@todo will be changed next versions` in
  // primevue/select 5.0.2; no upstream issue found), so Escape on a focused
  // Select never reaches the Dialog's document listener. With the list
  // closed, take the keystroke in the capture phase, before the Select sees
  // it, and send it on from the Select's parent. With the list open the
  // Select keeps it and closes only the list.
  select: {
    root: ({ instance }: { instance: { overlayVisible: boolean } }) => ({
      onKeydownCapture: (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || event.isComposing || instance.overlayVisible) return;
        const root = event.currentTarget as HTMLElement;
        event.stopPropagation();
        root.parentElement?.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            bubbles: true,
            cancelable: true,
          }),
        );
      },
    }),
  },
  step: {
    root: { 'aria-current': undefined },
    header: ({ context }: { context: { active: boolean } }) => ({
      'aria-selected': context.active,
      'aria-current': context.active ? 'step' : undefined,
    }),
  },
};
